/**
 * Error helpers for AG-UI Chat.
 *
 * Turns HTTP-status-bearing failures (e.g. a non-2xx response from the
 * chat/runner endpoint) into short, generic user-facing messages instead
 * of surfacing the raw response body in the chat transcript.
 */

/**
 * An error carrying the HTTP status of a failed request, so callers can
 * show a short, generic message instead of dumping the raw response body.
 */
export class HttpResponseError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: string,
  ) {
    super(message);
    this.name = 'HttpResponseError';
  }
}

/**
 * Formats an error for display in the chat UI.
 *
 * Errors carrying an HTTP status - whether our own HttpResponseError or an
 * error thrown by @ag-ui/client's HttpAgent (which attaches `.status` the
 * same way) - are reduced to a short, generic message. The full error,
 * including any raw response body, should still be logged to the console
 * by the caller for debugging; it must not end up in the rendered message.
 */
export function formatErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'status' in error && typeof (error as { status?: unknown }).status === 'number') {
    return `Something went wrong (HTTP ${(error as { status: number }).status}). Please try again.`;
  }
  return error instanceof Error ? error.message : 'Unknown error';
}
